import { useEffect } from "react";

import { BaseTinybirdMetricsType } from "@intelligems/ig-types/functions/analytics/analyze-resource-tinybird/analyze-resource-tinybird";
import { ExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import { VariationFe } from "@intelligems/ig-types/fe-only/variations";
import { getMaxZScore } from "@src/api/analytics/utils";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useOrgTimezone } from "@src/api/organizations/get-organization";
import { useHydrateAnalyticsProductFilters } from "@src/features/analytics/hooks/use-hydrate-analytics-product-filters";
import { useDispatch, useSelector } from "@src/store";
import {
  getEveryVariationHasAnyCheckoutBlock,
  getEveryVariationHasPostPurchase,
} from "@src/store/slices/checkoutBlocks/selectors";
import {
  setCompareGroups,
  setDashboardFilterByExperience,
  updateDashboardFilter,
} from "@src/store/slices/dashboardFilters";

export const useSetExperienceAnalyticsFilters = (
  experience: ExperienceFe,
  variations: VariationFe[],
  primaryMetricId: string,
  dashboardData?: BaseTinybirdMetricsType[]
) => {
  const dashboardFilters = useSelector((state) => state.dashboardFilters);
  const maxZScore = useConfiguration<number>("max_z_score");
  const dispatch = useDispatch();
  const timezone = useOrgTimezone();
  const variationIds = variations.map((variation) => variation.id);
  // A conversion-stage default is fair only when *every* variation carries a
  // block, so derive per-variation every… flags from the checkoutBlocks slice.
  // Any block type counts — they all emit the same checkout_block_viewed event.
  const everyVariationHasCheckoutBlock = useSelector(
    getEveryVariationHasAnyCheckoutBlock(variationIds)
  );
  const everyVariationHasPostPurchase = useSelector(
    getEveryVariationHasPostPurchase(variationIds)
  );
  // Both every… flags read the async checkoutBlocks slice, so gate the default
  // "User Behavior" filter until this experience's blocks hydrate — match by
  // variationId, not the slice's sticky global isSuccess flag. Applies to both
  // checkout-block and post-purchase tests; otherwise the default races.
  const checkoutBlocksLoaded = useSelector(
    (state) =>
      state.checkoutBlocks.checkoutBlocks.some((block) =>
        variationIds.includes(block.variationId)
      ) ||
      state.checkoutBlocks.postPurchaseBlocks.some((block) =>
        variationIds.includes(block.variationId)
      )
  );

  useHydrateAnalyticsProductFilters();

  useEffect(() => {
    if (
      (experience.testTypes.hasTestCheckoutBlocks ||
        experience.testTypes.hasTestPostPurchase) &&
      !checkoutBlocksLoaded
    ) {
      return;
    }
    if (dashboardFilters.experienceId !== experience.id) {
      dispatch(
        setDashboardFilterByExperience({
          experience,
          variations,
          dashboardData,
          maxZScore: getMaxZScore(
            dashboardFilters.maxZScore,
            dashboardFilters.isZScoreEnabled,
            maxZScore
          ).toString(),
          primaryMetricId,
          timezone,
          everyVariationHasCheckoutBlock,
          everyVariationHasPostPurchase,
        })
      );
    }
  }, [dashboardFilters.experienceId, experience.id, checkoutBlocksLoaded]);

  useEffect(() => {
    if (experience.category === "experiment") {
      if (!dashboardFilters.compareGroupA || !dashboardFilters.compareGroupB) {
        if (!dashboardFilters.maxZScore) {
          dispatch(
            updateDashboardFilter({
              key: "maxZScore",
              value: getMaxZScore(
                dashboardFilters.maxZScore,
                dashboardFilters.isZScoreEnabled,
                maxZScore
              ),
            })
          );
        }
        dispatch(
          setCompareGroups({
            variations,
            dashboardData,
            primaryMetricId,
          })
        );
      }
    }
  }, [dashboardData]);

  return experience;
};
